import { useMutation } from "@apollo/client";
import { useWS } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { alert } from "../components/common/Alert";
import INITIALIZE_STREAM from "../graphql/mutations/InitializeStream";

export function useStreamResult() {
  const { connectWS } = useWS();
  const [isStreaming, setIsStreaming] = useState(false);
  const [initializeStream] = useMutation(INITIALIZE_STREAM);

  const connectStream = async (updateCallback, finalizeCallback) => {
    const taskUuid = uuidv4();

    let response = await initializeStream({
      variables: {
        taskUuid,
      },
    });

    if (!response?.data?.initializeStream?.ok) {
      mixpanel.track("Stream Initialization Error", {
        streamingTaskUuid: taskUuid,
      });
      return { taskUuid: "", error: true };
    }

    const webSocket = connectWS(
      `llm-streaming-task/${taskUuid}`,
      false,
    );

    if (!webSocket) {
      mixpanel.track("Streaming task websocket error", {
        streamingTaskUuid: taskUuid,
      });
      return { taskUuid: "", error: true };
    }

    if (!webSocket) {
      return { taskUuid: "", error: true };
    }

    setIsStreaming(true);

    function reset() {
      webSocket.onclose();
      setIsStreaming(false);
      if (finalizeCallback) {
        finalizeCallback();
      }
    }

    webSocket.onmessage = function (event) {
      const newMessage = event?.data;

      if (!newMessage) {
        return;
      }

      // format is {"event": str, "data": dict }
      const parsedMessage = JSON.parse(newMessage);
      const type = parsedMessage?.event || "";
      const content = parsedMessage?.data?.content || "";

      if (type === "stream-end") {
        reset(); // Close the connection gracefully
        return;
      } else if (type === "stream-error") {
        alert(
          "error",
          "An error has occurred. Please try again later.",
        );
        mixpanel.track("Websocket Stream Error", {
          message: "Received 'stream-error' from server",
        });
        reset(); // Close the connection on error
        return;
      } else {
        updateCallback(content);
      }
    };

    webSocket.onerror = function () {
      mixpanel.track("Websocket Stream Error", {
        message:
          "Connection errored without message from server. This is likely a connection issue.",
      });
      alert(
        "error",
        "An error has occurred. Please check your connection try again later.",
      );
      reset(); // Close the connection if an error occurs
    };

    return { taskUuid, error: false };
  };

  return {
    connectStream,
    isStreaming,
  };
}
